import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { BaseOffersService } from '../../modules/offers/base-offers.service';
import { baseAuthSelectors, BaseCoreState, baseOffersActions } from '../../_store';
import { BaseFileDownloadService } from '../../shared/services/base-file-download.service';
import { BaseFileExtension } from '../../shared/enums/base-available-file-type.enum';

@Injectable()
export class BaseOffersEffects {
  loadOffersList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseOffersActions.loadOffersList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        let payload = {
          ...action.payload,
          customer: user.organizationId,
        };
        return this.offersService.getOffersList(payload).pipe(
          mergeMap((response) => [
            baseOffersActions.setOffersList({ payload: response }),
            baseOffersActions.successOffersList(),
          ]),
          catchError(() => of(baseOffersActions.errorOffersList()))
        );
      })
    );
  });

  loadOfferDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseOffersActions.loadOfferDetails),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        let payload = {
          offer: action.payload.offer,
          customer: user.organizationId,
        };
        return this.offersService.getOfferDetails(payload).pipe(
          mergeMap((response) => [
            baseOffersActions.setOfferDetails({ payload: response }),
            baseOffersActions.successOfferDetails(),
          ]),
          catchError(() => of(baseOffersActions.errorOfferDetails()))
        );
      })
    );
  });

  downloadOfferedDaysFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseOffersActions.downloadOfferedDaysFile),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$ = this.offersService.getCustomerOfferedDaysFile({
          customer: user.organizationId,
          offer: action.payload.offer,
        });

        return this.fileDownloadService
          .downloadFile(source$, {
            isPreview: action.payload.openInModal,
            documentType: BaseFileExtension.pdf,
          })
          .pipe(
            mergeMap((blob) => {
              const actions: Action[] = [baseOffersActions.successDownloadOfferedDaysFile()];

              if (blob) {
                actions.unshift(
                  baseOffersActions.setDownloadOfferedDaysFile({
                    payload: { document: blob },
                  })
                );
              }

              return actions;
            }),
            catchError(() => of(baseOffersActions.errorDownloadOfferedDaysFile()))
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private offersService: BaseOffersService,
    private fileDownloadService: BaseFileDownloadService
  ) {}
}
